import type { CSSProperties } from "react";

export type IconWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700;
/** Optical-size axis values supported by Material Symbols. */
export type IconSize = 20 | 24 | 40 | 48;

type IconProps = {
  /** Material Symbols name, e.g. "arrow_back", "search", "settings". */
  name: string;
  /** Pixel size (also drives the optical-size axis). */
  size?: IconSize;
  /** Variable-font weight axis. */
  weight?: IconWeight;
  /** Filled variant (FILL axis). */
  filled?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Marks the icon as semantic (read by assistive tech). */
  label?: string;
};

/**
 * Single Material Symbols icon.
 *
 * Color is inherited from `currentColor`, so set `text-grey-500` /
 * `text-red-600` etc. on a parent (or via `className`) to control it.
 */
export function Icon({
  name,
  size = 20,
  weight = 400,
  filled = false,
  className = "",
  style,
  label,
}: IconProps) {
  return (
    <span
      className={`material-symbols-outlined ${className}`}
      style={{
        fontSize: `${size}px`,
        fontVariationSettings: `'FILL' ${filled ? 1 : 0}, 'wght' ${weight}, 'opsz' ${size}`,
        ...style,
      }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {name}
    </span>
  );
}

/* Convenience aliases for the two arrows already used by Button / Field. */
export function ArrowLeft({ className }: { className?: string }) {
  return <Icon name="arrow_back" className={className} />;
}

export function ArrowRight({ className }: { className?: string }) {
  return <Icon name="arrow_forward" className={className} />;
}
